import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { AdminNav } from "../../components/admin/AdminNav";
import { FileUpload } from "../../components/admin/FileUpload";
import { toast } from "sonner";
import { Loader2, Upload, Plus, Trash2, Save, FileText } from "lucide-react";

const EMPTY = {
  slug: "", h1: "", subtitle: "", meta_title: "", meta_description: "", canonical_url: "",
  keyword_primary: "", keywords_secondary: [], category: "", tags: [],
  author: "Advanced Post Graduate Excellence", featured_image: "", featured_image_alt: "",
  body_html: "", summary_points: [], faq: [], related_ids: [], cta_course_slug: "",
  show_whatsapp: true, event_enabled: false, event_name: "", event_start: "",
  event_location: "", event_price: "", status: "draft", published_at: "",
};

const input = "editorial-input";
const overline = "overline mb-1 block";

export default function ArticleEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = id && id !== "nuovo";
  const fileRef = useRef(null);

  const [form, setForm] = useState(EMPTY);
  const [courses, setCourses] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/admin/articles/${id}`).then((r) => {
        setForm({ ...EMPTY, ...r.data });
        setLoading(false);
      }).catch(() => { toast.error("Articolo non trovato"); nav("/admin/blog"); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const importWord = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/articles/import-word", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        ...data,
        keywords_secondary: data.keywords_secondary || [],
        tags: data.tags || [],
        summary_points: data.summary_points || [],
        faq: data.faq || [],
      }));
      toast.success("Documento importato e strutturato dall'AI");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fallito");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (publish) => {
    if (!form.h1.trim()) { toast.error("Il titolo H1 è obbligatorio"); return; }
    setSaving(true);
    const payload = { ...form, status: publish ? "published" : form.status };
    try {
      let res;
      if (isEdit) res = await api.put(`/admin/articles/${id}`, payload);
      else res = await api.post("/admin/articles", payload);
      toast.success(isEdit ? "Articolo aggiornato" : "Articolo creato");
      nav("/admin/blog");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // FAQ helpers
  const addFaq = () => setForm((f) => ({ ...f, faq: [...f.faq, { question: "", answer: "" }] }));
  const updFaq = (i, k, v) => setForm((f) => ({ ...f, faq: f.faq.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const rmFaq = (i) => setForm((f) => ({ ...f, faq: f.faq.filter((_, j) => j !== i) }));

  const toggleRelated = (aid) => setForm((f) => ({
    ...f, related_ids: f.related_ids.includes(aid) ? f.related_ids.filter((x) => x !== aid) : [...f.related_ids, aid],
  }));

  if (loading) return <div className="min-h-screen bg-[#F4F8FF]"><AdminNav /><div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1452F0]" /></div></div>;

  return (
    <div className="min-h-screen bg-[#F4F8FF]">
      <AdminNav />
      <div className="px-6 md:px-10 max-w-[1000px] mx-auto py-10">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <h1 className="font-heading text-4xl font-light text-[#0B1220]">{isEdit ? "Modifica articolo" : "Nuovo articolo"}</h1>
          <div className="flex items-center gap-3">
            <button onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn" className="border border-[#0B1220] text-[#0B1220] hover:bg-[#0B1220] hover:text-white transition-colors rounded-sm px-5 py-2.5 text-sm font-medium flex items-center gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva bozza
            </button>
            <button onClick={() => save(true)} disabled={saving} data-testid="publish-btn" className="bg-[#1452F0] text-white hover:bg-[#0E3DC0] transition-colors rounded-sm px-5 py-2.5 text-sm font-medium flex items-center gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Pubblica
            </button>
          </div>
        </div>

        {/* Word import */}
        <div className="bg-[#E9EFFA] border border-dashed border-[#1452F0]/40 rounded-sm p-6 mb-8 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <FileText className="h-6 w-6 text-[#1452F0] mt-0.5" />
            <div>
              <div className="font-medium text-[#0B1220]">Importa da Word (.docx)</div>
              <div className="text-sm text-[#5C6678]">L'AI struttura automaticamente titoli, H2/H3, meta, keyword e FAQ. Potrai poi modificare tutto.</div>
            </div>
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-word-btn" className="bg-[#0B1220] text-white rounded-sm px-5 py-2.5 text-sm font-medium flex items-center gap-2">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {importing ? "Elaborazione…" : "Carica .docx"}
          </button>
          <input ref={fileRef} type="file" accept=".docx" onChange={importWord} className="hidden" data-testid="import-word-input" />
        </div>

        <div className="space-y-8">
          {/* Core */}
          <section className="bg-white rounded-sm border border-[#D5DEEE] p-6 space-y-5">
            <div>
              <label className={overline}>Titolo (H1) *</label>
              <input data-testid="article-h1-input" value={form.h1} onChange={set("h1")} className={input} placeholder="Titolo principale dell'articolo" />
            </div>
            <div>
              <label className={overline}>Sottotitolo</label>
              <input data-testid="article-subtitle-input" value={form.subtitle} onChange={set("subtitle")} className={input} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={overline}>Slug URL (/blog/…)</label>
                <input data-testid="article-slug-input" value={form.slug} onChange={set("slug")} className={input} placeholder="lascia vuoto per generarlo dal titolo" />
              </div>
              <div>
                <label className={overline}>Categoria</label>
                <input data-testid="article-category-input" value={form.category} onChange={set("category")} className={input} />
              </div>
            </div>
            <div>
              <label className={overline}>Contenuto (HTML: h2, h3, p, ul…)</label>
              <textarea data-testid="article-body-input" rows={16} value={form.body_html} onChange={set("body_html")} className={`${input} font-mono-ibn text-sm resize-y`} placeholder="<h2>…</h2><p>…</p>" />
              <p className="text-xs text-[#8A94A6] mt-1">Nessun &lt;h1&gt; nel corpo: l'H1 è il titolo qui sopra.</p>
            </div>
          </section>

          {/* SEO */}
          <section className="bg-white rounded-sm border border-[#D5DEEE] p-6 space-y-5">
            <h2 className="font-heading text-2xl text-[#0B1220]">SEO</h2>
            <div>
              <label className={overline}>Meta title</label>
              <input data-testid="article-metatitle-input" value={form.meta_title} onChange={set("meta_title")} className={input} maxLength={70} />
              <p className="text-xs text-[#8A94A6] mt-1">{form.meta_title.length}/60 consigliati</p>
            </div>
            <div>
              <label className={overline}>Meta description</label>
              <textarea data-testid="article-metadesc-input" rows={2} value={form.meta_description} onChange={set("meta_description")} className={`${input} resize-none`} maxLength={200} />
              <p className="text-xs text-[#8A94A6] mt-1">{form.meta_description.length}/155 consigliati</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={overline}>Keyword principale</label>
                <input data-testid="article-kwprimary-input" value={form.keyword_primary} onChange={set("keyword_primary")} className={input} />
              </div>
              <div>
                <label className={overline}>Keyword secondarie (separate da virgola)</label>
                <input data-testid="article-kwsecondary-input" value={(form.keywords_secondary || []).join(", ")} onChange={(e) => setVal("keywords_secondary", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={input} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={overline}>Tag (separati da virgola)</label>
                <input data-testid="article-tags-input" value={(form.tags || []).join(", ")} onChange={(e) => setVal("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={input} />
              </div>
              <div>
                <label className={overline}>Autore</label>
                <input data-testid="article-author-input" value={form.author} onChange={set("author")} className={input} />
              </div>
            </div>
            <div>
              <label className={overline}>Canonical URL (opzionale)</label>
              <input data-testid="article-canonical-input" value={form.canonical_url} onChange={set("canonical_url")} className={input} placeholder="lascia vuoto per usare l'URL dell'articolo" />
            </div>
          </section>

          {/* Image */}
          <section className="bg-white rounded-sm border border-[#D5DEEE] p-6 space-y-5">
            <h2 className="font-heading text-2xl text-[#0B1220]">Immagine in evidenza</h2>
            <FileUpload value={form.featured_image} onChange={(v) => setVal("featured_image", v)} label="Immagine (convertita in WebP)" testid="article-image-upload" />
            <div>
              <label className={overline}>Alt text immagine</label>
              <input data-testid="article-imagealt-input" value={form.featured_image_alt} onChange={set("featured_image_alt")} className={input} placeholder="Descrizione dell'immagine per SEO/accessibilità" />
            </div>
          </section>

          {/* In sintesi */}
          <section className="bg-white rounded-sm border border-[#D5DEEE] p-6 space-y-3">
            <h2 className="font-heading text-2xl text-[#0B1220]">Box "In sintesi"</h2>
            <label className={overline}>Un punto per riga</label>
            <textarea data-testid="article-summary-input" rows={4} value={(form.summary_points || []).join("\n")} onChange={(e) => setVal("summary_points", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} className={`${input} resize-y`} />
          </section>

          {/* FAQ */}
          <section className="bg-white rounded-sm border border-[#D5DEEE] p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl text-[#0B1220]">FAQ (schema FAQPage)</h2>
              <button onClick={addFaq} data-testid="add-faq-btn" className="text-sm text-[#1452F0] inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Aggiungi</button>
            </div>
            {(form.faq || []).map((f, i) => (
              <div key={i} className="border border-[#D5DEEE] rounded-sm p-4 space-y-2" data-testid={`faq-item-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="overline">Domanda {i + 1}</span>
                  <button onClick={() => rmFaq(i)} className="text-[#A63C3C]" data-testid={`remove-faq-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                <input value={f.question} onChange={(e) => updFaq(i, "question", e.target.value)} className={input} placeholder="Domanda" data-testid={`faq-q-${i}`} />
                <textarea rows={2} value={f.answer} onChange={(e) => updFaq(i, "answer", e.target.value)} className={`${input} resize-none`} placeholder="Risposta" data-testid={`faq-a-${i}`} />
              </div>
            ))}
          </section>

          {/* Links & CTA */}
          <section className="bg-white rounded-sm border border-[#D5DEEE] p-6 space-y-5">
            <h2 className="font-heading text-2xl text-[#0B1220]">CTA & collegamenti</h2>
            <div>
              <label className={overline}>Corso collegato (CTA "Iscriviti")</label>
              <select data-testid="article-course-select" value={form.cta_course_slug} onChange={set("cta_course_slug")} className={input}>
                <option value="">— Nessuno —</option>
                {courses.map((c) => <option key={c.id} value={c.slug}>{c.title}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.show_whatsapp} onChange={(e) => setVal("show_whatsapp", e.target.checked)} data-testid="article-whatsapp-toggle" className="h-4 w-4 accent-[#1452F0]" />
              <span className="text-sm text-[#0B1220]">Mostra pulsante WhatsApp</span>
            </label>
            {articles.filter((x) => x.id !== id).length > 0 && (
              <div>
                <label className={overline}>Articoli correlati</label>
                <div className="grid sm:grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto">
                  {articles.filter((x) => x.id !== id).map((x) => (
                    <label key={x.id} className="flex items-center gap-2 text-sm text-[#0B1220] cursor-pointer">
                      <input type="checkbox" checked={form.related_ids.includes(x.id)} onChange={() => toggleRelated(x.id)} className="h-4 w-4 accent-[#1452F0]" data-testid={`related-${x.id}`} />
                      <span className="truncate">{x.h1}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Event schema */}
          <section className="bg-white rounded-sm border border-[#D5DEEE] p-6 space-y-5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.event_enabled} onChange={(e) => setVal("event_enabled", e.target.checked)} data-testid="article-event-toggle" className="h-4 w-4 accent-[#1452F0]" />
              <span className="font-heading text-2xl text-[#0B1220]">Dati evento (schema Event)</span>
            </label>
            {form.event_enabled && (
              <div className="grid sm:grid-cols-2 gap-5">
                <div><label className={overline}>Nome evento</label><input value={form.event_name} onChange={set("event_name")} className={input} data-testid="event-name" /></div>
                <div><label className={overline}>Data/ora inizio (ISO)</label><input value={form.event_start} onChange={set("event_start")} className={input} placeholder="2026-10-15T09:00:00" data-testid="event-start" /></div>
                <div><label className={overline}>Luogo</label><input value={form.event_location} onChange={set("event_location")} className={input} data-testid="event-location" /></div>
                <div><label className={overline}>Prezzo</label><input value={form.event_price} onChange={set("event_price")} className={input} placeholder="€ 1.500" data-testid="event-price" /></div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
